import { ChakraProvider, Box, VStack, HStack, Heading, Container, Flex, IconButton, useDisclosure, Button, useBreakpointValue, Text, Tooltip } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import theme from './theme';
import AudioPlayer from './components/AudioPlayer';
import ExpandedNotePanel from './components/ExpandedNotePanel';
import TransportControls from './components/TransportControls';
import FileImport from './components/FileImport';
import BlindTestView from './components/BlindTestView';
import InfoOverlay from './components/InfoOverlay';
import ShareButton from './components/ShareButton';
import DownloadButton from './components/DownloadButton';
import AddFilesButton from './components/AddFilesButton';
import ReloadButton from './components/ReloadButton';
import Feedback from './components/Feedback';
import GoogleAnalytics from './components/GoogleAnalytics';
import { AudioProvider, useAudioContext } from './context/AudioContext';
import { SharedDataProvider, useSharedData } from './context/SharedDataContext';
import { NotesProvider, useNotes } from './context/NotesContext';
import { DisplayNamesProvider, useDisplayNames } from './context/DisplayNamesContext';
import { RegionsProvider, useRegions } from './context/RegionsContext';
import { PageTitleProvider, usePageTitle } from './context/PageTitleContext';
import { setupKeyboardHandlers } from './utils/playerSwitching';
import { matchAudioLevels } from './utils/levelMatching';
import WaveSurfer from 'wavesurfer.js';
import { FaShieldAlt, FaQuestionCircle } from 'react-icons/fa';
import SiteInfoModal from './components/SiteInfoModal';
import MobileInfoOverlay from './components/MobileInfoOverlay';
import BuyMeACoffeeButton from './components/BuyMeACoffeeButton';
import { FileService, LoadedFile, ShareData } from './services/FileService';
import { SharingService } from './services/SharingService';
import LoadingSpinner from './components/LoadingSpinner';
import { SyncProvider, useSync } from './features/sync/context/SyncContext';
import Logo from './components/Logo';
import PageTitle from './components/PageTitle';
import MasterFilterModal from './components/MasterFilterModal';

import { naturalSortStrings } from './utils/sorting';
import { isIOS, isAndroidMobile, getMobileDownloadWarnings } from './utils/deviceDetection';
import MobileDownloadWarning from './components/MobileDownloadWarning';
import { simplifyFilenames } from './utils/filenameSimplifier';
import { simplifyLoadedFileFilenames } from './utils/filenameSimplificationHelper';
import { formatLoadingMessage } from './utils/loadingMessageFormatter';

// Utility function to extract filename from URL (same logic as DirectFileLinkImportModal)
const extractFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const parts = pathname.split('/').filter(p => p);
    
    if (parts.length > 0) {
      let fileName = decodeURIComponent(parts[parts.length - 1]);
      // Remove query parameters if any
      const queryIndex = fileName.indexOf('?');
      if (queryIndex > 0) {
        fileName = fileName.substring(0, queryIndex);
      }
      
      if (fileName && fileName.length < 200 && fileName !== url) {
        return fileName;
      }
    }
  } catch {
    // URL parsing failed
  }
  
  // Fallback: try to extract from the URL string directly
  try {
    const lastSlash = url.lastIndexOf('/');
    if (lastSlash >= 0 && lastSlash < url.length - 1) {
      const potentialName = url.substring(lastSlash + 1);
      const queryIndex = potentialName.indexOf('?');
      let fileName = queryIndex > 0 ? potentialName.substring(0, queryIndex) : potentialName;
      fileName = decodeURIComponent(fileName);
      
      if (fileName && fileName.length < 200 && fileName !== url) {
        return fileName;
      }
    }
  } catch {
    // Decoding failed
  }
  
  return 'audio-file';
};
import SelectiveFileLoadingModal from './components/SelectiveFileLoadingModal';
import RegionSetupModal from './components/RegionSetupModal';
import LZString from 'lz-string';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import KMDeck from './pages/KMDeck';
import Embed from './pages/Embed';
import PatchPlan from './pages/PatchPlan';
import { UrlCleaner } from './utils/urlCleaner';
import TooltipKeyBox from './components/TooltipKeyBox';
import SnowEffect from './components/SnowEffect';

// Check if current date is within Christmas period (Dec 15 - Jan 5)
const isChristmasPeriod = (): boolean => {
  const now = new Date();
  const month = now.getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12
  const day = now.getDate();
  
  // December 15-31
  if (month === 12 && day >= 15) {
    return true;
  }
  
  // January 1-5
  if (month === 1 && day <= 5) {
    return true;
  }
  
  return false;
};

const AppContent = () => {
  const location = useLocation();
  const { shareData: sharedData } = useSharedData();
  const {
    isPlaying,
    playAll,
    pauseAll,
    stopAll,
    forward,
    rewind,
    rewindToLastStart,
    selectedPlayer,
    selectPlayer,
    audioElements,
    setGainOffsets,
    setGainOffsetsWithFileMapping,
    isBlindTestMode,
    toggleBlindTestMode,
    removePlayer,
    remapGainOffsets,
    resetMono,
    stereoMode,
    toggleStereoMode,
    isMono,
    toggleMono,
    filterType,
    filterFrequency,
    filterQ,
    filterSlope,
    filterFrequencyLow,
    filterFrequencyHigh,
    filterInputGain,
    setMasterFilter,
    resetMasterFilter,
    toggleMasterFilter,
    hasFilterBeenSet,
  } = useAudioContext();
  
  const { pageTitle } = usePageTitle();
  const { customDisplayNames, setDisplayName, clearDisplayName, migrateDisplayNamesFromTrackNumbers } = useDisplayNames();
  const { migrateNotesFromTrackNumbers } = useNotes();
  const { migrateRegionsFromTrackNumbers, getOpenRegion, endOpenRegion } = useRegions();
  
  const { isOpen: isRegionModalOpen, onOpen: onRegionModalOpen, onClose: onRegionModalClose } = useDisclosure();
  
  // Handler for creating regions (R key)
  const handleCreateRegion = useCallback(() => {
    const fileId = playerNumberToFileId.current.get(selectedPlayer);
    if (!fileId) return;
    
    const audioElement = audioElements.get(selectedPlayer);
    if (!audioElement) return;
    
    const currentTime = audioElement.currentTime;
    const openRegion = getOpenRegion(fileId);
    
    // Check if current position is directly after an open region
    // Only show end option if we're at or after the open region's start time
    // (meaning we're within or past the open region)
    const isDirectlyAfterOpenRegion = openRegion !== null && currentTime >= openRegion.startTime;
    
    // If we have an open region and we're at/after its start, end it directly
    if (isDirectlyAfterOpenRegion && openRegion !== null) {
      // End the region directly without opening modal
      endOpenRegion(fileId, currentTime);
      return;
    }
    
    // Store these values for the modal
    (window as any).__regionModalData = {
      fileId,
      currentTime,
      hasOpenRegion: false,
      editingRegion: null,
    };
    
    onRegionModalOpen();
  }, [selectedPlayer, audioElements, getOpenRegion, onRegionModalOpen]);
  
  // Listen for custom event to open region modal (from clicking region segment)
  useEffect(() => {
    const handleOpenRegionModal = () => {
      onRegionModalOpen();
    };
    
    window.addEventListener('openRegionModal', handleOpenRegionModal);
    return () => {
      window.removeEventListener('openRegionModal', handleOpenRegionModal);
    };
  }, [onRegionModalOpen]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a']
    },
    onDrop: (files) => {
      handleFilesSelected(files, files.map(() => ''), files.map(() => ''));
    },
    noClick: true,
    noKeyboard: true
  });

  const [loadedFiles, setLoadedFiles] = useState<LoadedFile[]>([]);
  
  // Create a mapping from playerNumber to fileId for components that need it
  const playerNumberToFileId = useRef(new Map<number, string>());
  useEffect(() => {
    playerNumberToFileId.current.clear();
    loadedFiles.forEach((file, index) => {
      playerNumberToFileId.current.set(index + 1, file.id);
    });
    // Migrate display names, notes, and regions from track numbers to file IDs when files are loaded
    if (loadedFiles.length > 0) {
      migrateDisplayNamesFromTrackNumbers(loadedFiles);
      migrateNotesFromTrackNumbers(loadedFiles);
      migrateRegionsFromTrackNumbers(loadedFiles);
    }
  }, [loadedFiles, migrateDisplayNamesFromTrackNumbers, migrateNotesFromTrackNumbers, migrateRegionsFromTrackNumbers]);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRefs = useRef<Map<number, WaveSurfer>>(new Map());
  const [readyPlayers, setReadyPlayers] = useState<Set<number>>(new Set());
  const [showInfoOverlay, setShowInfoOverlay] = useState(true);
  const [forceInfoOverlay, setForceInfoOverlay] = useState(false);
  const { isOpen: isPrivacyModalOpen, onOpen: onPrivacyModalOpen, onClose: onPrivacyModalClose } = useDisclosure();
  const { isOpen: isFilterModalOpen, onOpen: onFilterModalOpen, onClose: onFilterModalClose } = useDisclosure();
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [isLoadingSharedSetup, setIsLoadingSharedSetup] = useState(false);
  const [sharedProgress, setSharedProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  const [sharedDownloadedBytes, setSharedDownloadedBytes] = useState(0);
  const [sharedTotalBytes, setSharedTotalBytes] = useState(0);
  
  // State for tracking when adding more files
  const [isAddingFiles, setIsAddingFiles] = useState(false);
  const [addingFilesDownloadedBytes, setAddingFilesDownloadedBytes] = useState(0);
  const [addingFilesTotalBytes, setAddingFilesTotalBytes] = useState(0);
  const [addingFilesCompletedCount, setAddingFilesCompletedCount] = useState(0);
  const [addingFilesTotalCount, setAddingFilesTotalCount] = useState(0);
  
  // Debug: Track isAddingFiles changes
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.tsx:207',message:'isAddingFiles state changed',data:{isAddingFiles,addingFilesDownloadedBytes,addingFilesTotalBytes},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
    // #endregion
  }, [isAddingFiles, addingFilesDownloadedBytes, addingFilesTotalBytes]);
  const isIOSMobileDevice = isMobile && isIOS();
  const mobileViewportHeight = isAndroidMobile() ? '100svh' : '100vh';
  
  // Mobile warning state for shared setups
  const [showSharedSetupMobileWarning, setShowSharedSetupMobileWarning] = useState(false);
  const [pendingSharedSetupData, setPendingSharedSetupData] = useState<string | null>(null);
  const [pendingSharedSetupSize, setPendingSharedSetupSize] = useState(0);
  
  // Selective file loading state
  const [showSelectiveFileModal, setShowSelectiveFileModal] = useState(false);
  const [selectiveFileData, setSelectiveFileData] = useState<string | null>(null);
  const [selectiveFileList, setSelectiveFileList] = useState<Array<{id: string; name: string; size: number; duration?: number; url: string}>>([]);
  const showLargePreview = !isBlindTestMode && loadedFiles.length <= 2 && selectedPlayer > 0;
  const [playersGeneration, setPlayersGeneration] = useState(0);

  // Add state for mobile drag-and-drop
  const [draggedPlayerIndex, setDraggedPlayerIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragY, setDragY] = useState<number | null>(null);
  const dragTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [dragReady, setDragReady] = useState(false);
  const dragStartYRef = useRef<number | null>(null);

  // Add state for desktop drag-and-drop (separate from mobile)
  const [desktopDraggedIndex, setDesktopDraggedIndex] = useState<number | null>(null);
  const [desktopDragOverIndex, setDesktopDragOverIndex] = useState<number | null>(null);
  const [isDesktopDragging, setIsDesktopDragging] = useState(false);

  const handlePlayerTouchStart = (index: number) => (e: React.TouchEvent) => {
    if (!isMobile) return;
    if (draggedPlayerIndex !== null) return; // Already dragging
    dragStartYRef.current = e.touches[0].clientY;
    setDragReady(false);
    dragTimerRef.current = setTimeout(() => {
      setDraggedPlayerIndex(index);
      setDragY(e.touches[0].clientY);
      setDragReady(true);
    }, 500); // 0.5s long-press
  };

  const handlePlayerTouchMove = (index: number) => (e: React.TouchEvent) => {
    if (!isMobile) return;
    const currentY = e.touches[0].clientY;
    if (!dragReady) {
      // If finger moves before long-press, cancel drag
      if (dragStartYRef.current !== null && Math.abs(currentY - dragStartYRef.current) > 10) {
        if (dragTimerRef.current) {
          clearTimeout(dragTimerRef.current);
          dragTimerRef.current = null;
        }
        dragStartYRef.current = null;
      }
      return;
    }
    // Only do drag logic if dragReady is true
    setDragY(currentY);
    // Find which player is being hovered over
    const playerBoxes = Array.from(document.querySelectorAll('[data-player-number]')) as HTMLElement[];
    for (let i = 0; i < playerBoxes.length; i++) {
      const rect = playerBoxes[i].getBoundingClientRect();
      if (currentY >= rect.top && currentY <= rect.bottom) {
        setDragOverIndex(i);
        break;
      }
    }
  };

  const handlePlayerTouchEnd = (index: number) => (e: React.TouchEvent) => {
    if (!isMobile) return;
    if (dragTimerRef.current) {
      clearTimeout(dragTimerRef.current);
      dragTimerRef.current = null;
    }
    dragStartYRef.current = null;
    if (dragReady && draggedPlayerIndex !== null && dragOverIndex !== null && draggedPlayerIndex !== dragOverIndex) {
      setLoadedFiles(prev => {
        const newFiles = [...prev];
        const [moved] = newFiles.splice(draggedPlayerIndex, 1);
        newFiles.splice(dragOverIndex, 0, moved);
        // After reordering, update selectedPlayer to the new index of the moved file
        const movedFileId = moved.id;
        const updatedIndex = newFiles.findIndex(f => f.id === movedFileId);
        if (updatedIndex !== -1) {
          selectPlayer(updatedIndex + 1);
        }

        // Remap gain offsets to track with the reordered files
        remapGainOffsets(newFiles);

        // Do NOT increment playersGeneration here; only do so for add/remove
        return newFiles;
      });
      setReadyPlayers(new Set());
    }
    setDraggedPlayerIndex(null);
    setDragOverIndex(null);
    setDragY(null);
    setDragReady(false);
    document.body.style.userSelect = '';
  };

  // Desktop drag handlers
  const handleDesktopDragStart = (index: number) => (e: React.DragEvent) => {
    if (isMobile) return; // Only for desktop
    
    setDesktopDraggedIndex(index);
    setIsDesktopDragging(true);
    
    // Create an empty div element to completely eliminate the flying circle/ghost image
    const emptyDiv = document.createElement('div');
    emptyDiv.style.width = '1px';
    emptyDiv.style.height = '1px';
    emptyDiv.style.opacity = '0';
    emptyDiv.style.position = 'absolute';
    emptyDiv.style.top = '-1000px';
    document.body.appendChild(emptyDiv);
    
    // Use the empty div as drag image and clean it up immediately
    e.dataTransfer.setDragImage(emptyDiv, 0, 0);
    setTimeout(() => {
      if (document.body.contains(emptyDiv)) {
        document.body.removeChild(emptyDiv);
      }
    }, 0);
    
    // Set drag effect
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDesktopDragOver = (index: number) => (e: React.DragEvent) => {
    if (isMobile || !isDesktopDragging) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    setDesktopDragOverIndex(index);
  };

  const handleDesktopDragLeave = (index: number) => (e: React.DragEvent) => {
    if (isMobile || !isDesktopDragging) return;
    
    // Only clear drag over if we're leaving this specific element
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (desktopDragOverIndex === index) {
        setDesktopDragOverIndex(null);
      }
    }
  };

  const handleDesktopDrop = (index: number) => (e: React.DragEvent) => {
    if (isMobile || !isDesktopDragging) return;
    
    e.preventDefault();
    
    if (desktopDraggedIndex !== null && desktopDraggedIndex !== index) {
      // Use the same reordering logic as mobile and keyboard
      setLoadedFiles(prev => {
        const newFiles = [...prev];
        const [moved] = newFiles.splice(desktopDraggedIndex, 1);
        newFiles.splice(index, 0, moved);
        
        // After reordering, update selectedPlayer to the new index of the moved file
        const movedFileId = moved.id;
        const updatedIndex = newFiles.findIndex(f => f.id === movedFileId);
        if (updatedIndex !== -1) {
          selectPlayer(updatedIndex + 1);
        }
        
        // Remap gain offsets to track with the reordered files
        remapGainOffsets(newFiles);
        
        // Do NOT increment playersGeneration here; only do so for add/remove
        return newFiles;
      });
      setReadyPlayers(new Set());
    }
    
    // Clean up drag state
    setDesktopDraggedIndex(null);
    setDesktopDragOverIndex(null);
    setIsDesktopDragging(false);
  };

  const handleDesktopDragEnd = () => {
    // Clean up drag state even if drop didn't occur
    setDesktopDraggedIndex(null);
    setDesktopDragOverIndex(null);
    setIsDesktopDragging(false);
  };

  // Prevent text selection on long-press drag
  useEffect(() => {
    if (dragReady) {
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.userSelect = '';
    }
    return () => {
      document.body.style.userSelect = '';
    };
  }, [dragReady]);

  const handleShowInfoOverlay = () => {
    localStorage.removeItem('infoOverlayShown');
    localStorage.removeItem('mobileInfoOverlayShown');
    setForceInfoOverlay(true);
  };

  useEffect(() => {
    if (forceInfoOverlay) {
      const timer = setTimeout(() => {
        setForceInfoOverlay(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [forceInfoOverlay]);

  useEffect(() => {
    if (loadedFiles.length > 0 && readyPlayers.size > 0) {
      setShowInfoOverlay(true);
      setForceInfoOverlay(false);
    }
  }, [loadedFiles.length, readyPlayers.size]);

  // Note: Selective file loading check is now handled in loadSharedSetup useEffect
  // to prevent files from loading before the modal appears

  // Handle shared setup loading
  useEffect(() => {
    const loadSharedSetup = async () => {
      const data = SharingService.getShareDataFromUrl();
      if (!data) return;  // Early return if no shared setup data

      try {
        // Parse the share data to get the stored file size for mobile warnings
        const decompressedData = LZString.decompressFromEncodedURIComponent(data);
        if (!decompressedData) {
          throw new Error('Failed to decompress share data');
        }
        const shareData = JSON.parse(decompressedData) as ShareData;
        
        // Check for selective file loading BEFORE loading files
        const totalSizeMB = shareData.totalFileSize ? shareData.totalFileSize / (1024 * 1024) : 0;
        const fileCount = shareData.tracks ? shareData.tracks.length : 0;
        const shouldShowSelective = (shareData.allowFileSelection && fileCount >= 4) || 
                                   (totalSizeMB >= 100 && fileCount >= 4);
        
        if (shouldShowSelective) {
          console.log('[App] Selective file loading triggered:', { 
            totalSizeMB, 
            fileCount, 
            allowFileSelection: shareData.allowFileSelection,
            reason: shareData.allowFileSelection ? 'allowFileSelection enabled' : 'large file size'
          });
          
          // Prepare file list for selective loading modal
          // Use custom display names from shareData.displayNames if available, otherwise use track.name
          const fileList = shareData.tracks.map(track => {
            const trackId = track.id.toString();
            const customDisplayName = shareData.displayNames?.[trackId];
            const displayName = customDisplayName || track.name || `track-${track.id}`;
            
            return {
              id: trackId,
              name: displayName,
              size: 0, // We'll need to fetch this
              duration: undefined, // We'll need to fetch this
              url: track.shareableUrl
            };
          });
          
          setSelectiveFileData(data);
          setSelectiveFileList(fileList);
          setShowSelectiveFileModal(true);
          return; // Don't load files yet - wait for user selection
        }
        
        // Check for mobile warnings using stored file size
        if (shareData.totalFileSize && shareData.totalFileSize > 0) {
          const warnings = getMobileDownloadWarnings(shareData.totalFileSize);
          console.log('[App] Shared setup mobile warnings:', warnings);
          
          if (warnings.shouldWarnDataUsage || warnings.shouldWarnLargeSize) {
            // Store the data and show warning modal
            setPendingSharedSetupData(data);
            setPendingSharedSetupSize(shareData.totalFileSize);
            setShowSharedSetupMobileWarning(true);
            return;
          }
        }
        
        // Proceed with loading if no warnings or no size data
        setIsLoadingSharedSetup(true);
        setSharedDownloadedBytes(0);
        setSharedTotalBytes(0);
        const { files, shareData: loadedShareData } = await FileService.loadSharedSetup(
          data,
          (completed, total) => setSharedProgress({ completed, total }),
          (downloadedBytes, totalBytes) => {
            setSharedDownloadedBytes(downloadedBytes);
            setSharedTotalBytes(totalBytes);
          }
        );
        
        // Files are already in the correct order from ShareData.tracks, so don't sort them
        // This preserves any custom reordering that was done before sharing
        
        // Simplify filenames for direct link files
        const simplifiedFiles = simplifyLoadedFileFilenames(files);
        
        // Set the loaded files and share data
        setLoadedFiles(simplifiedFiles);
        setShareData(loadedShareData);
        
        // Increment generation to trigger level matching for shared setup
        setPlayersGeneration(g => g + 1);
        
        // Reset the ready players set
        setReadyPlayers(new Set());
        
        // Wait for all files to be processed and ready, with a safety timeout
        const timeoutPromise = new Promise<void>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout waiting for players to be ready'));
          }, 10000); // 10 second timeout
        });
        
        try {
          await Promise.race([
            new Promise<void>((resolve) => {
              const checkReady = () => {
                if (readyPlayers.size === files.length) {
                  resolve();
                } else {
                  setTimeout(checkReady, 100);
                }
              };
              checkReady();
            }),
            timeoutPromise
          ]);
        } catch (error) {
          console.warn('Loading timeout reached, continuing anyway:', error);
          // We continue execution even if timeout occurs
        }

        // After files are loaded, scroll to the first player
        if (containerRef.current) {
          const firstPlayer = containerRef.current.querySelector('[data-player-number="1"]') as HTMLElement;
          if (firstPlayer) {
            // Calculate the position within the container and scroll container instead of using scrollIntoView
            const containerRect = containerRef.current.getBoundingClientRect();
            const playerRect = firstPlayer.getBoundingClientRect();
            const relativeTop = playerRect.top - containerRect.top;
            const scrollTop = containerRef.current.scrollTop + relativeTop - (containerRect.height / 2) + (playerRect.height / 2);
            
            containerRef.current.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          }
        }
        
        // Ensure loading spinner is removed even if the readyPlayers check never completes
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
        
        // Clean the URL to hide share data parameters
        UrlCleaner.cleanUrl();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorStack = error instanceof Error ? error.stack : undefined;
        console.error(`Failed to load shared setup: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
      }
    };

    // Load shared setup on mount
    loadSharedSetup();
  }, []);

  // Handle mobile warning for shared setups
  const handleSharedSetupMobileWarningContinue = async () => {
    if (pendingSharedSetupData) {
      setShowSharedSetupMobileWarning(false);
      try {
        setIsLoadingSharedSetup(true);
        setSharedDownloadedBytes(0);
        setSharedTotalBytes(0);
        const { files, shareData } = await FileService.loadSharedSetup(
          pendingSharedSetupData,
          (completed, total) => setSharedProgress({ completed, total }),
          (downloadedBytes, totalBytes) => {
            setSharedDownloadedBytes(downloadedBytes);
            setSharedTotalBytes(totalBytes);
          }
        );
        
        // Simplify filenames for direct link files
        const simplifiedFiles = simplifyLoadedFileFilenames(files);
        
        // Set the loaded files and share data
        setLoadedFiles(simplifiedFiles);
        setShareData(shareData);
        
        // Increment generation to trigger level matching for shared setup
        setPlayersGeneration(g => g + 1);
        
        // Reset the ready players set
        setReadyPlayers(new Set());
        
        // Wait for all files to be processed and ready, with a safety timeout
        const timeoutPromise = new Promise<void>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout waiting for players to be ready'));
          }, 10000); // 10 second timeout
        });
        
        try {
          await Promise.race([
            new Promise<void>((resolve) => {
              const checkReady = () => {
                if (readyPlayers.size === simplifiedFiles.length) {
                  resolve();
                } else {
                  setTimeout(checkReady, 100);
                }
              };
              checkReady();
            }),
            timeoutPromise
          ]);
        } catch (error) {
          console.warn('Loading timeout reached, continuing anyway:', error);
        }

        // After files are loaded, scroll to the first player
        if (containerRef.current) {
          const firstPlayer = containerRef.current.querySelector('[data-player-number="1"]') as HTMLElement;
          if (firstPlayer) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const playerRect = firstPlayer.getBoundingClientRect();
            const relativeTop = playerRect.top - containerRect.top;
            const scrollTop = containerRef.current.scrollTop + relativeTop - (containerRect.height / 2) + (playerRect.height / 2);
            
            containerRef.current.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          }
        }
        
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
        
        // Clean the URL to hide share data parameters
        UrlCleaner.cleanUrl();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorStack = error instanceof Error ? error.stack : undefined;
        console.error(`Failed to load shared setup after warning: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
      }
      setPendingSharedSetupData(null);
      setPendingSharedSetupSize(0);
    }
  };

  const handleSharedSetupMobileWarningClose = () => {
    setShowSharedSetupMobileWarning(false);
    setPendingSharedSetupData(null);
    setPendingSharedSetupSize(0);
  };

  // Selective file loading handlers
  const handleSelectiveFileLoadAll = async () => {
    if (selectiveFileData) {
      setShowSelectiveFileModal(false);
      
      try {
        setIsLoadingSharedSetup(true);
        const { files, shareData: loadedShareData } = await FileService.loadSharedSetup(
          selectiveFileData,
          (completed, total) => setSharedProgress({ completed, total }),
          (downloadedBytes, totalBytes) => {
            setSharedDownloadedBytes(downloadedBytes);
            setSharedTotalBytes(totalBytes);
          }
        );
        
        // Simplify filenames for direct link files
        const simplifiedFiles = simplifyLoadedFileFilenames(files);
        
        setLoadedFiles(simplifiedFiles);
        setShareData(loadedShareData);
        setPlayersGeneration(g => g + 1);
        setReadyPlayers(new Set());
        
        // Wait for players to be ready
        const timeoutPromise = new Promise<void>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout waiting for players to be ready'));
          }, 10000);
        });
        
        try {
          await Promise.race([
            new Promise<void>((resolve) => {
              const checkReady = () => {
                if (readyPlayers.size === files.length) {
                  resolve();
                } else {
                  setTimeout(checkReady, 100);
                }
              };
              checkReady();
            }),
            timeoutPromise
          ]);
        } catch (error) {
          console.warn('Loading timeout reached, continuing anyway:', error);
        }
        
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
        
        // Clean the URL to hide share data parameters
        UrlCleaner.cleanUrl();
      } catch (error) {
        console.error('Failed to load all files:', error);
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
      }
    }
  };

  const handleSelectiveFileLoadSelected = async (selectedFileIds: string[]) => {
    if (selectiveFileData) {
      setShowSelectiveFileModal(false);
      
      try {
        setIsLoadingSharedSetup(true);
        const { files, shareData: loadedShareData } = await FileService.loadSharedSetupSelective(
          selectiveFileData,
          selectedFileIds,
          (completed, total) => setSharedProgress({ completed, total }),
          (downloadedBytes, totalBytes) => {
            setSharedDownloadedBytes(downloadedBytes);
            setSharedTotalBytes(totalBytes);
          }
        );
        
        // Simplify filenames for direct link files
        const simplifiedFiles = simplifyLoadedFileFilenames(files);
        
        setLoadedFiles(simplifiedFiles);
        setShareData(loadedShareData);
        setPlayersGeneration(g => g + 1);
        setReadyPlayers(new Set());
        
        // Wait for players to be ready
        const timeoutPromise = new Promise<void>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout waiting for players to be ready'));
          }, 10000);
        });
        
        try {
          await Promise.race([
            new Promise<void>((resolve) => {
              const checkReady = () => {
                if (readyPlayers.size === files.length) {
                  resolve();
                } else {
                  setTimeout(checkReady, 100);
                }
              };
              checkReady();
            }),
            timeoutPromise
          ]);
        } catch (error) {
          console.warn('Loading timeout reached, continuing anyway:', error);
        }
        
        // After files are loaded, scroll to the first player
        if (containerRef.current) {
          const firstPlayer = containerRef.current.querySelector('[data-player-number="1"]') as HTMLElement;
          if (firstPlayer) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const playerRect = firstPlayer.getBoundingClientRect();
            const relativeTop = playerRect.top - containerRect.top;
            const scrollTop = containerRef.current.scrollTop + relativeTop - (containerRect.height / 2) + (playerRect.height / 2);
            
            containerRef.current.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          }
        }
        
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
        
        // Clean the URL to hide share data parameters
        UrlCleaner.cleanUrl();
      } catch (error) {
        console.error('Failed to load selected files:', error);
        setIsLoadingSharedSetup(false);
        setSharedProgress({ completed: 0, total: 0 });
      }
    }
  };

  const handleSelectiveFileModalClose = () => {
    setShowSelectiveFileModal(false);
    setSelectiveFileData(null);
    setSelectiveFileList([]);
  };

  // Keep this separate effect for handling loading state as an additional safety measure
  // On mobile, dismiss loading spinner once files are loaded even if audio context isn't ready
  useEffect(() => {
    if (loadedFiles.length > 0) {
      if (isMobile) {
        // On mobile, dismiss spinner after a short delay to allow UI to settle
        // This prevents the spinner from staying indefinitely due to audio context requirements
        const timer = setTimeout(() => {
          setIsLoadingSharedSetup(false);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        // On desktop, wait for all players to be ready
        if (readyPlayers.size === loadedFiles.length) {
          setIsLoadingSharedSetup(false);
        }
      }
    }
  }, [loadedFiles.length, readyPlayers.size, isMobile]);

  // Auto-scroll to selected player
  useEffect(() => {
    // Only auto-scroll if there are more than 2 players, otherwise both players are visible
    if (containerRef.current && selectedPlayer > 0 && loadedFiles.length > 2) {
      const playerElement = containerRef.current.querySelector(`[data-player-number="${selectedPlayer}"]`) as HTMLElement;
      if (playerElement) {
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const playerRect = playerElement.getBoundingClientRect();
        
        const relativeTop = playerRect.top - containerRect.top;
        const isLastPlayer = selectedPlayer === loadedFiles.length;
        const targetScroll = isLastPlayer
          ? container.scrollTop + relativeTop
          : container.scrollTop + relativeTop - (containerRect.height - playerRect.height) / 2;
        
        // Log scrolling information for debugging
        console.log('Auto-scroll triggered:', {
          playerNumber: selectedPlayer,
          containerTop: containerRect.top,
          playerTop: playerRect.top,
          relativeTop,
          containerHeight: containerRect.height,
          playerHeight: playerRect.height,
          containerScrollTop: container.scrollTop,
          targetScroll
        });
        
        // Force layout recalculation before scrolling
        setTimeout(() => {
          container.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });
        }, 100); // Increased timeout to ensure DOM has updated
      }
    }
  }, [selectedPlayer, loadedFiles.length]);

  // Add a second effect to ensure scrolling works when the component is re-rendered
  useEffect(() => {
    // Only auto-scroll if there are more than 2 players, otherwise both players are visible
    if (containerRef.current && selectedPlayer > 0 && loadedFiles.length > 2) {
      // Handle edge case where the first scroll might not work
      const delayedScroll = setTimeout(() => {
        const playerElement = containerRef.current?.querySelector(`[data-player-number="${selectedPlayer}"]`) as HTMLElement;
        if (playerElement && containerRef.current) {
          // Calculate the position within the container and scroll container instead of using scrollIntoView
          const containerRect = containerRef.current.getBoundingClientRect();
          const playerRect = playerElement.getBoundingClientRect();
          const relativeTop = playerRect.top - containerRect.top;
          const scrollTop = containerRef.current.scrollTop + relativeTop - (containerRect.height / 2) + (playerRect.height / 2);
          
          containerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }
      }, 300);
      
      return () => clearTimeout(delayedScroll);
    }
  }, [selectedPlayer, loadedFiles.length]);

  // Track if level matching has been applied for the current set of files
  const levelMatchingAppliedRef = useRef<number>(0);
  // Track the generation of the currently running calculation to cancel stale ones
  const levelMatchingInProgressRef = useRef<number | null>(null);

  useEffect(() => {
    console.log(`[Level Matching] ${readyPlayers.size}/${loadedFiles.length} players ready`);
    
    // Only run level matching if all players are ready AND we haven't applied it for this generation of players
    if (readyPlayers.size === loadedFiles.length && 
        loadedFiles.length > 0 && 
        levelMatchingAppliedRef.current !== playersGeneration) {
      console.log('[Level Matching] All players ready, running level matching...');
      // Capture the current generation to check if it changed during calculation
      const currentGeneration = playersGeneration;
      
      // Cancel any previous calculation by updating the ref to the latest generation
      // This allows the latest calculation to proceed and cancels old ones
      levelMatchingInProgressRef.current = currentGeneration;
      
      const applyLevelMatching = async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check if this calculation has been superseded by a newer one
        // If levelMatchingInProgressRef was updated to a different generation, we've been cancelled
        if (levelMatchingInProgressRef.current !== currentGeneration) {
          console.log('[Level Matching] Calculation cancelled - newer deletion occurred');
          return;
        }
        
        // Also check if the generation state changed (double-check)
        if (playersGeneration !== currentGeneration) {
          console.log('[Level Matching] Generation changed during delay, skipping calculation');
          return;
        }
        
        const results = await matchAudioLevels(wavesurferRefs.current);
        
        // Check again if this calculation is still the latest one
        if (levelMatchingInProgressRef.current !== currentGeneration) {
          console.log('[Level Matching] Calculation cancelled during RMS calculation - newer deletion occurred');
          return;
        }
        
        if (playersGeneration !== currentGeneration) {
          console.log('[Level Matching] Generation changed during RMS calculation - newer deletion occurred');
          return;
        }
        
        // Create offset map using sequential player numbers (1, 2, 3...) to match renumbering system
        const offsetMap = new Map<number, number>();
        const sortedResults = results.sort((a, b) => a.playerNumber - b.playerNumber);
        sortedResults.forEach((result, index) => {
          const sequentialPlayerNumber = index + 1;
          offsetMap.set(sequentialPlayerNumber, result.gainReduction);
        });
        
        console.log('[Level Matching] Applied gain offsets:', Object.fromEntries(offsetMap));
        console.log('[Level Matching] Current selected player:', selectedPlayer);
        setGainOffsetsWithFileMapping(new Map(offsetMap), loadedFiles);
        console.log('[Level Matching] Gain offsets have been applied to audio context');
        
        // Mark as applied only after successful completion
        // This ensures that if deletions happen rapidly, the latest calculation will complete
        levelMatchingAppliedRef.current = currentGeneration;
        levelMatchingInProgressRef.current = null;
      };
      
      applyLevelMatching().catch(error => {
        console.error('[Level Matching] Error:', error);
        // On error, still mark as applied to prevent infinite retries
        // But only if we're still the current calculation
        if (levelMatchingInProgressRef.current === currentGeneration) {
          levelMatchingAppliedRef.current = currentGeneration;
          levelMatchingInProgressRef.current = null;
        }
      });
    }
  }, [readyPlayers, loadedFiles.length, playersGeneration, setGainOffsetsWithFileMapping, selectedPlayer, loadedFiles]);

  const handleFilesSelected = (files: File[], urls: string[], shareableUrls: string[]) => {
    console.log(`[File Selection] Adding ${files.length} new files`);
    
    // Stop and reset all existing playback to ensure sync when new files are added
    if (loadedFiles.length > 0) {
      console.log('[File Selection] Stopping and resetting existing playback for sync');
      stopAll();
    }
    
    // Reset mono to stereo when new files are loaded
    resetMono();
    
    const newLoadedFiles = FileService.handleFilesSelected(files, urls, shareableUrls);
    
    // Check if any new files are from direct links (have URLs)
    const hasDirectLinks = newLoadedFiles.some(f => f.originalUrl || f.shareableUrl);
    
    if (hasDirectLinks) {
      // Get all direct link files (existing + new)
      setLoadedFiles(prev => {
        const allDirectLinkFiles = [
          ...prev.filter(f => f.originalUrl || f.shareableUrl),
          ...newLoadedFiles.filter(f => f.originalUrl || f.shareableUrl)
        ];
        
        if (allDirectLinkFiles.length >= 2) {
          // Extract original filenames from URLs (not from File.name which might be simplified)
          const directLinkFilenames = allDirectLinkFiles.map(f => {
            const url = f.originalUrl || f.shareableUrl || '';
            const extracted = extractFileNameFromUrl(url);
            console.log('[Filename Simplification] Extracted filename from URL:', url.substring(0, 50) + '...', '->', extracted);
            return extracted;
          });
          
          console.log('[Filename Simplification] Original filenames:', directLinkFilenames);
          
          // Simplify all direct link filenames together
          const simplifiedFilenames = simplifyFilenames(directLinkFilenames);
          
          console.log('[Filename Simplification] Simplified filenames:', simplifiedFilenames);
          
          // Create a map of URL to simplified filename for lookup
          const urlToSimplifiedMap = new Map<string, string>();
          allDirectLinkFiles.forEach((f, index) => {
            const url = f.originalUrl || f.shareableUrl || '';
            urlToSimplifiedMap.set(url, simplifiedFilenames[index]);
            console.log('[Filename Simplification] Mapping URL to simplified name:', url.substring(0, 50) + '...', '->', simplifiedFilenames[index]);
          });
          
          // Update existing direct link files with simplified names
          const updatedExisting = prev.map(f => {
            if (f.originalUrl || f.shareableUrl) {
              const url = f.originalUrl || f.shareableUrl || '';
              const simplifiedName = urlToSimplifiedMap.get(url);
              console.log('[Filename Simplification] Existing file:', f.file.name, 'URL:', url.substring(0, 50) + '...', 'Simplified:', simplifiedName);
              if (simplifiedName && simplifiedName !== f.file.name) {
                // Create new File object with simplified name
                console.log('[Filename Simplification] Updating existing file from', f.file.name, 'to', simplifiedName);
                const newFile = new File([f.file], simplifiedName, { type: f.file.type });
                return { ...f, file: newFile };
              }
            }
            return f;
          });
          
          // Update new files with simplified names
          const updatedNewFiles = newLoadedFiles.map(f => {
            if (f.originalUrl || f.shareableUrl) {
              const url = f.originalUrl || f.shareableUrl || '';
              const simplifiedName = urlToSimplifiedMap.get(url);
              console.log('[Filename Simplification] New file:', f.file.name, 'URL:', url.substring(0, 50) + '...', 'Simplified:', simplifiedName);
              if (simplifiedName && simplifiedName !== f.file.name) {
                // Create new File object with simplified name
                console.log('[Filename Simplification] Updating new file from', f.file.name, 'to', simplifiedName);
                const newFile = new File([f.file], simplifiedName, { type: f.file.type });
                return { ...f, file: newFile };
              }
            }
            return f;
          });
          
          // Sort new files alphabetically by filename using natural sort
          const sortedNewFiles = [...updatedNewFiles].sort((a, b) => 
            naturalSortStrings(a.file.name, b.file.name)
          );
          
          const updated = [...updatedExisting, ...sortedNewFiles];
          setPlayersGeneration(g => g + 1); // increment generation on file change
          return updated;
        } else {
          // Not enough direct link files to simplify, just add new files normally
          const sortedNewFiles = [...newLoadedFiles].sort((a, b) => 
            naturalSortStrings(a.file.name, b.file.name)
          );
          const updated = [...prev, ...sortedNewFiles];
          setPlayersGeneration(g => g + 1); // increment generation on file change
          return updated;
        }
      });
    } else {
      // No direct links, just add files normally
      const sortedNewFiles = [...newLoadedFiles].sort((a, b) => 
        naturalSortStrings(a.file.name, b.file.name)
      );
      setLoadedFiles(prev => {
        const updated = [...prev, ...sortedNewFiles];
        setPlayersGeneration(g => g + 1); // increment generation on file change
        return updated;
      });
    }
    // Don't reset readyPlayers - keep existing ready players and wait for new ones
  };

  const handlePlayerReady = useCallback((wavesurfer: WaveSurfer, playerNumber: number) => {
    console.log(`[Player Ready] Player ${playerNumber} ready (${wavesurfer.getDuration()}s)`);
    wavesurferRefs.current.set(playerNumber, wavesurfer);
    setReadyPlayers(prev => {
      const newSet = new Set(prev);
      newSet.add(playerNumber);
      return newSet;
    });
  }, [loadedFiles.length]);

  const handlePlayerRemove = useCallback((trackNumber: number) => {
    // Get the file ID for the removed player and clear its display name
    const removedFile = loadedFiles[trackNumber - 1];
    const removedFileId = removedFile?.id;
    if (removedFileId) {
      clearDisplayName(removedFileId);
    }
    
    // Calculate the new file order after deletion
    const newFiles = [...loadedFiles];
    newFiles.splice(trackNumber - 1, 1);
    
    setLoadedFiles(newFiles);
    setPlayersGeneration(g => {
      const newGen = g + 1;
      // Don't set levelMatchingAppliedRef here - allow level matching to recalculate
      // after deletion since the deleted player should be removed from the calculation
      // and new gains should be applied if it was the quietest file
      return newGen;
    }); // increment generation on file change
    
    // Call removePlayer first to update audio element mappings
    removePlayer(trackNumber);
    
    // Now rebuild wavesurferRefs by matching WaveSurfer media elements to audio elements
    // After removePlayer, audioElements will have the correct new player numbers
    const mediaElementToWavesurfer = new Map<HTMLAudioElement, WaveSurfer>();
    Array.from(wavesurferRefs.current.values()).forEach(wavesurfer => {
      const mediaElement = wavesurfer.getMediaElement();
      if (mediaElement) {
        mediaElementToWavesurfer.set(mediaElement, wavesurfer);
      }
    });
    
    // Rebuild wavesurferRefs by matching media elements to the renumbered audio elements
    const newWavesurferRefs = new Map<number, WaveSurfer>();
    audioElements.forEach((audioElement, newPlayerNumber) => {
      const wavesurfer = mediaElementToWavesurfer.get(audioElement);
      if (wavesurfer) {
        newWavesurferRefs.set(newPlayerNumber, wavesurfer);
      }
    });
    
    wavesurferRefs.current.clear();
    newWavesurferRefs.forEach((wavesurfer, index) => {
      wavesurferRefs.current.set(index, wavesurfer);
    });
    
    // Rebuild readyPlayers based on which WaveSurfer instances are ready
    const newReadyPlayers = new Set<number>();
    newWavesurferRefs.forEach((wavesurfer, playerNumber) => {
      if (wavesurfer && wavesurfer.getDuration() > 0) {
        newReadyPlayers.add(playerNumber);
      }
    });
    
    setReadyPlayers(newReadyPlayers);
    
    // Level matching will recalculate after deletion when all players are ready
    // The deleted player will be removed from the calculation and new gains will be applied
    // to all remaining players if the deleted player was the quietest file
  }, [removePlayer, loadedFiles, clearDisplayName]);

  const handlePlayerSelect = useCallback((reverse: boolean) => {
    if (audioElements.size < 2) return;
    
    const validPlayers = Array.from(audioElements.keys()).sort((a, b) => a - b);
    const currentIndex = validPlayers.indexOf(selectedPlayer);
    const nextIndex = reverse 
      ? (currentIndex - 1 + validPlayers.length) % validPlayers.length
      : (currentIndex + 1) % validPlayers.length;
    const nextPlayer = validPlayers[nextIndex];
    
    console.log(`Switching from player ${selectedPlayer} to ${nextPlayer}`);
    
    // Select the next player
    selectPlayer(nextPlayer);
    
    // Only manually scroll if there are more than 2 players, otherwise both players are visible
    if (loadedFiles.length > 2) {
      setTimeout(() => {
        if (containerRef.current) {
          const playerElement = containerRef.current.querySelector(`[data-player-number="${nextPlayer}"]`) as HTMLElement;
          if (playerElement) {
            console.log(`Manually scrolling to player ${nextPlayer}`);
            playerElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 150);
    }
  }, [audioElements, selectedPlayer, selectPlayer]);

  const handleReorderPlayer = useCallback((direction: 'up' | 'down') => {
    setLoadedFiles(prev => {
      const currentIndex = selectedPlayer - 1;
      if (currentIndex < 0 || currentIndex >= prev.length) return prev;
      const newIndex = direction === 'up'
        ? Math.max(0, currentIndex - 1)
        : Math.min(prev.length - 1, currentIndex + 1);
      if (newIndex === currentIndex) return prev;
      const newFiles = [...prev];
      const [moved] = newFiles.splice(currentIndex, 1);
      newFiles.splice(newIndex, 0, moved);

      // After reordering, update selectedPlayer to the new index of the moved file
      const movedFileId = moved.id;
      const updatedIndex = newFiles.findIndex(f => f.id === movedFileId);
      if (updatedIndex !== -1) {
        selectPlayer(updatedIndex + 1);
      }

      // Remap gain offsets to track with the reordered files
      remapGainOffsets(newFiles);

      // Do NOT increment playersGeneration here; only do so for add/remove
      return newFiles;
    });
  }, [selectedPlayer, selectPlayer, remapGainOffsets]);

  // Track last F key press for double-tap detection
  const lastFKeyPressRef = useRef<number>(0);
  const fKeyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fKeyTimeoutRef.current) {
        clearTimeout(fKeyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip hotkeys when user is editing text
      if (e.target instanceof HTMLInputElement || 
          e.target instanceof HTMLTextAreaElement ||
          (e.target instanceof HTMLElement && e.target.contentEditable === 'true')) {
        return;
      }
      
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          handleReorderPlayer('up');
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          handleReorderPlayer('down');
        }
      }
      
      // Handle F key for filter modal (only when not using Cmd/Ctrl)
      if ((e.key === 'f' || e.key === 'F') && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        if (loadedFiles.length > 0) {
          // If modal is open, close it immediately
          if (isFilterModalOpen) {
            // Clear any pending timeout
            if (fKeyTimeoutRef.current) {
              clearTimeout(fKeyTimeoutRef.current);
              fKeyTimeoutRef.current = null;
            }
            lastFKeyPressRef.current = 0;
            onFilterModalClose();
            return;
          }
          
          const now = Date.now();
          const timeSinceLastPress = now - lastFKeyPressRef.current;
          
          // Check for double-tap (within 300ms, similar to double-click)
          if (timeSinceLastPress < 300 && timeSinceLastPress > 0) {
            // Double-tap detected - cancel the pending modal open and toggle filter
            if (fKeyTimeoutRef.current) {
              clearTimeout(fKeyTimeoutRef.current);
              fKeyTimeoutRef.current = null;
            }
            lastFKeyPressRef.current = 0;
            toggleMasterFilter();
          } else {
            // First F press - wait to see if it's a double-tap
            lastFKeyPressRef.current = now;
            
            // Clear any existing timeout
            if (fKeyTimeoutRef.current) {
              clearTimeout(fKeyTimeoutRef.current);
            }
            
            // Set timeout to open modal after 300ms (if no second press)
            fKeyTimeoutRef.current = setTimeout(() => {
              // Single F press confirmed after timeout
              onFilterModalOpen();
              lastFKeyPressRef.current = 0;
              fKeyTimeoutRef.current = null;
            }, 300);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Clean up any pending timeout
      if (fKeyTimeoutRef.current) {
        clearTimeout(fKeyTimeoutRef.current);
        fKeyTimeoutRef.current = null;
      }
    };
  }, [handleReorderPlayer, loadedFiles.length, onFilterModalOpen, onFilterModalClose, isFilterModalOpen, toggleMasterFilter]);


  // Set canonical tag and robots meta tag for homepage
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasDataParam = searchParams.has('data') || searchParams.has('encrypted') || location.pathname.startsWith('/s/');
    
    // If URL has data parameter, set noindex immediately (shared URLs should not be indexed)
    if (hasDataParam) {
      let robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
      if (!robotsTag) {
        robotsTag = document.createElement('meta');
        robotsTag.setAttribute('name', 'robots');
        document.head.appendChild(robotsTag);
      }
      robotsTag.setAttribute('content', 'noindex, nofollow');
      
      // Set canonical to clean URL without query params
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      
      const pathname = location.pathname;
      const baseUrl = 'https://abmymix.com';
      if (pathname === '/privacy' || pathname.startsWith('/privacy')) {
        canonicalLink.setAttribute('href', baseUrl + '/privacy');
      } else if (pathname === '/terms' || pathname.startsWith('/terms')) {
        canonicalLink.setAttribute('href', baseUrl + '/terms');
      } else if (pathname === '/KMDeck' || pathname.startsWith('/KMDeck') || pathname.startsWith('/kmdeck')) {
        canonicalLink.setAttribute('href', baseUrl + '/KMDeck');
      } else if (pathname === '/patchplan' || pathname.startsWith('/patchplan')) {
        canonicalLink.setAttribute('href', baseUrl + '/patchplan');
      } else {
        canonicalLink.setAttribute('href', baseUrl + '/');
      }
      return;
    }
    
    const isHomepage = location.pathname === '/' || 
      (!location.pathname.startsWith('/privacy') && 
       !location.pathname.startsWith('/terms') && 
       !location.pathname.startsWith('/KMDeck') && 
       !location.pathname.startsWith('/embed') && 
       !location.pathname.startsWith('/patchplan'));
    
    if (isHomepage) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', 'https://abmymix.com/');
      
      // Set robots meta tag: allow indexing only when no files are loaded
      let robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
      if (!robotsTag) {
        robotsTag = document.createElement('meta');
        robotsTag.setAttribute('name', 'robots');
        document.head.appendChild(robotsTag);
      }
      
      // If files are loaded, set noindex; otherwise allow indexing
      if (loadedFiles.length > 0) {
        robotsTag.setAttribute('content', 'noindex, nofollow');
      } else {
        // Remove noindex to allow indexing of the import page
        robotsTag.setAttribute('content', 'index, follow');
      }
    }
  }, [location.pathname, location.search, loadedFiles.length]);

  // Check if we're on the homepage (not on other routes)
  const isHomepage = location.pathname === '/' || 
    (!location.pathname.startsWith('/privacy') && 
     !location.pathname.startsWith('/terms') && 
     !location.pathname.startsWith('/KMDeck') && 
     !location.pathname.startsWith('/embed') && 
     !location.pathname.startsWith('/patchplan'));

  // Show snow effect only during Christmas period (Dec 15 - Jan 5), on homepage, and on import page (no files loaded)
  const shouldShowSnow = isHomepage && isChristmasPeriod() && loadedFiles.length === 0;

  return (
    <SyncProvider audioElements={audioElements}>
      <Flex 
        direction="column" 
        h={{ base: mobileViewportHeight, md: "100vh" }}
        maxH={{ base: mobileViewportHeight, md: "100vh" }}
        minW={{ base: "auto", md: "780px" }}
        bg="gray.900" 
        overflow="hidden"
        position="relative"
      >
        {shouldShowSnow && <SnowEffect />}
        {isLoadingSharedSetup && (
          <LoadingSpinner 
            message={formatLoadingMessage('Loading shared setup...', {
              totalBytes: sharedTotalBytes,
              downloadedBytes: sharedDownloadedBytes,
              progress: sharedProgress,
            })}
          />
        )}
        {isAddingFiles && (
          <LoadingSpinner 
            message={formatLoadingMessage('Importing files...', {
              totalBytes: addingFilesTotalBytes,
              downloadedBytes: addingFilesDownloadedBytes,
            })}
          />
        )}
        {isMobile ? (
          <MobileInfoOverlay
            hasLoadedFiles={loadedFiles.length > 0}
            onDismiss={() => {
              setShowInfoOverlay(false);
              setForceInfoOverlay(false);
            }}
            onDontShowAgain={() => {
              setShowInfoOverlay(false);
              setForceInfoOverlay(false);
            }}
            readyPlayers={readyPlayers}
            force={forceInfoOverlay}
            onShown={() => {
              // Remove the onShown handler since we want to keep force true until dismissed
            }}
          />
        ) : (
          <InfoOverlay
            hasLoadedFiles={loadedFiles.length > 0}
            onDismiss={() => {
              setShowInfoOverlay(false);
              setForceInfoOverlay(false);
            }}
            onDontShowAgain={() => {
              setShowInfoOverlay(false);
              setForceInfoOverlay(false);
            }}
            readyPlayers={readyPlayers}
            force={forceInfoOverlay}
            onShown={() => {
              // Remove the onShown handler since we want to keep force true until dismissed
            }}
          />
        )}
        
        <Box 
          flex="1" 
          overflow="hidden"
          position="relative"
        >
          <Container 
            maxW="container.xl" 
            h="full"
            py={loadedFiles.length === 0 ? "35px" : "10px"}
            display="flex" 
            flexDirection="column"
          >
            <Flex justify="center" align="center" mb={3} position="relative">
              {loadedFiles.length > 0 && !isBlindTestMode ? (
                <>
                  {isMobile ? (
                    shareData ? (
                      <>
                        {shareData.allowDownloads && (
                          <Box position="absolute" left="60px" zIndex={2}>
                            <DownloadButton
                              originalUrls={shareData.originalUrls}
                              isOwner={shareData.isOwner}
                              allowDownloads={shareData.allowDownloads}
                            />
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box position="absolute" left="60px" zIndex={2}>
                        <AddFilesButton
                          onFilesSelected={handleFilesSelected}
                          loadedFiles={loadedFiles}
                          onLoadingChange={setIsAddingFiles}
                          onProgressChange={(downloaded, total, completedFiles, totalFiles) => {
                            setAddingFilesDownloadedBytes(downloaded);
                            setAddingFilesTotalBytes(total);
                            setAddingFilesCompletedCount(completedFiles || 0);
                            setAddingFilesTotalCount(totalFiles || 0);
                          }}
                        />
                      </Box>
                    )
                  ) : null}
                </>
              ) : null}
              <Tooltip 
                label={
                  <HStack spacing={2} color="gray.800">
                    <Text>Start afresh</Text>
                    <TooltipKeyBox>⌘R</TooltipKeyBox>
                  </HStack>
                } 
                placement="bottom"
              >
                <Box
                  as="button"
                  onClick={() => window.location.href = '/'}
                  flexShrink={0}
                  display={isBlindTestMode || loadedFiles.length === 0 ? 'none' : 'block'}
                  position="relative"
                  mb={1}
                  bg="transparent"
                  border="none"
                  cursor="pointer"
                  _hover={{ opacity: 0.9 }}
                  _active={{ opacity: 0.8 }}
                  zIndex={1}
                >
                  <Box
                    w="180px"
                    h="60px"
                    position="relative"
                    sx={{
                      filter: 'drop-shadow(0 0 6px rgba(229, 193, 150, 0.15))',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        filter: 'drop-shadow(0 0 8px rgba(229, 193, 150, 0.2))',
                      }
                    }}
                  >
                    <Logo />
                  </Box>
                </Box>
              </Tooltip>
              <Box position="absolute" left={0} top={0}>
                {loadedFiles.length > 0 && (
                  <Feedback 
                    onPrivacyClick={onPrivacyModalOpen}
                    onShowInfoOverlay={handleShowInfoOverlay}
                  />
                )}
              </Box>
              <Box position="absolute" right={0} top={0}>
                {loadedFiles.length > 0 && (
                  <VStack spacing={2} align="flex-end">
                    <HStack spacing={2} align="flex-start">
                      <BuyMeACoffeeButton />
                    </HStack>
                  </VStack>
                )}
              </Box>
              {loadedFiles.length > 0 ? (
                <>
                  {isMobile && !isBlindTestMode ? (
                    <Box position="absolute" right="60px" zIndex={2}>
                      <ShareButton loadedFiles={loadedFiles} />
                    </Box>
                  ) : null}
                </>
              ) : null}
            </Flex>
            
            {loadedFiles.length === 0 ? (
              <>
                <FileImport 
                  onFilesSelected={handleFilesSelected}
                  onLoadingChange={setIsAddingFiles}
                  onProgressChange={(downloaded, total, completedFiles, totalFiles) => {
                    setAddingFilesDownloadedBytes(downloaded);
                    setAddingFilesTotalBytes(total);
                    setAddingFilesCompletedCount(completedFiles || 0);
                    setAddingFilesTotalCount(totalFiles || 0);
                  }}
                />
              </>
            ) : (
              <Flex direction="column" h="full">
                <PageTitle />
                <Box 
                  ref={containerRef}
                  data-testid="player-container"
                  flex="1" 
                  overflowY="auto"
                  overflowX="visible"
                  px={4}
                  py={2}
                  display={isBlindTestMode ? 'none' : 'block'}
                  h={{ base: "calc(100% - 10px)", md: "full" }}
                  sx={{
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      bg: 'gray.900',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bg: 'gray.700',
                      borderRadius: '2px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      bg: 'gray.600',
                    },
                    // Ensure scrolling behavior works
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    // --- Highlight for drag-over ---
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bg: isDragActive ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
                      transition: 'all 0.2s',
                      pointerEvents: 'none',
                      zIndex: 1,
                    },
                  }}
                  {...(loadedFiles.length > 0 ? getRootProps() : {})}
                >
                  <input {...getInputProps()} />
                  <VStack 
                    spacing={
                      isMobile && pageTitle.trim() && loadedFiles.length <= 2 
                        ? 5 // 20px instead of 24px (4px reduction)
                        : 6 // Default 24px spacing
                    } 
                    pb={20} 
                    pt={desktopDragOverIndex === 0 && desktopDraggedIndex !== null && desktopDraggedIndex !== 0 ? "20px" : 0}
                    maxW="2xl" 
                    mx="auto" 
                    position="relative"
                  >
                    {/* Drop indicator for the very first position */}
                    {desktopDragOverIndex === 0 && desktopDraggedIndex !== null && desktopDraggedIndex !== 0 && (
                      <Box
                        position="absolute"
                        top="7px"
                        left="0"
                        right="0"
                        height="3px"
                        bg="#3182ce"
                        borderRadius="2px"
                        boxShadow="0 2px 4px rgba(49, 130, 206, 0.3)"
                        zIndex="10"
                      />
                    )}
                    
                    {loadedFiles.map((loadedFile, index) => {
                      const shouldReduceSpacing = isMobile && pageTitle.trim() && loadedFiles.length <= 2;
                      const isMobileDragged = draggedPlayerIndex === index;
                      const isDesktopDragged = desktopDraggedIndex === index;
                      const isDesktopDragTarget = desktopDragOverIndex === index && desktopDraggedIndex !== index;
                      const isDragged = isMobileDragged || isDesktopDragged;
                      
                      return (
                        <Box 
                          key={loadedFile.id}
                          position="relative" 
                          w="full"
                          // Mobile touch handlers
                          onTouchStart={handlePlayerTouchStart(index)}
                          onTouchMove={handlePlayerTouchMove(index)}
                          onTouchEnd={handlePlayerTouchEnd(index)}
                          onTouchCancel={handlePlayerTouchEnd(index)}
                          // Desktop drag handlers
                          draggable={!isMobile}
                          onDragStart={handleDesktopDragStart(index)}
                          onDragOver={handleDesktopDragOver(index)}
                          onDragLeave={handleDesktopDragLeave(index)}
                          onDrop={handleDesktopDrop(index)}
                          onDragEnd={handleDesktopDragEnd}
                          style={{
                            zIndex: isDragged ? 100 : undefined,
                            opacity: isDragged ? 0.7 : 1,
                            touchAction: dragReady ? 'none' : undefined,
                            WebkitUserSelect: 'none',
                            userSelect: 'none',
                            cursor: !isMobile ? 'grab' : undefined,
                          }}
                          // Add conditional margin reduction
                          my={shouldReduceSpacing ? "-0.5" : "0"}
                        >
                          {/* Drop indicator positioned above the target player, higher up (but not for first player) */}
                          {isDesktopDragTarget && index !== 0 && (
                            <Box
                              position="absolute"
                              top="-13px"
                              left="0"
                              right="0"
                              height="3px"
                              bg="#3182ce"
                              borderRadius="2px"
                              boxShadow="0 2px 4px rgba(49, 130, 206, 0.3)"
                              zIndex="10"
                            />
                          )}
                          
                          <AudioPlayer
                          id={loadedFile.id}
                          trackNumber={index + 1}
                          file={loadedFile.file}
                          originalUrl={loadedFile.originalUrl}
                          shareableUrl={loadedFile.shareableUrl}
                          onWavesurferReady={(wavesurfer) => handlePlayerReady(wavesurfer, index + 1)}
                          onRemove={handlePlayerRemove}
                          data-player-number={index + 1}
                          isDragged={isDragged}
                          {...(loadedFile.shareableUrl && 
                               loadedFile.shareableUrl !== '' && 
                               loadedFile.shareableUrl.length > 0 ? { 'data-shareable-url': loadedFile.shareableUrl } : {})}
                          />
                        </Box>
                      );
                    })}
                    <Box h={{ base: "160px", md: 0 }} w="100%" />
                  </VStack>
                </Box>

                {isBlindTestMode && (
                  <Box flex="1">
                    <BlindTestView playerNumberToFileId={playerNumberToFileId.current} />
                  </Box>
                )}
              </Flex>
            )}
          </Container>
        </Box>
        
        {loadedFiles.length > 0 && (
          <Box 
            borderTop="1px" 
            borderColor="gray.700" 
            bg="gray.900" 
            pt={6}
            pb={(() => {
              const shouldReduceBottomSpacing = isMobile && pageTitle.trim() && loadedFiles.length <= 2;
              if (isMobile) {
                // iOS browsers need extra bottom padding for browser chrome and home indicator
                const basePadding = isIOSMobileDevice ? 80 : 24;
                const reduction = shouldReduceBottomSpacing ? 15 : 0;
                return `${basePadding - reduction}px`;
              }
              return 6; // Desktop default
            })()}
            boxShadow="0 -4px 20px rgba(0, 0, 0, 0.3)"
            position="relative"
            width="100%"
            zIndex={2}
          >
            {/* Show expanded note panel when there are 1-2 cloud storage players (Dropbox/Google Drive) - positioned above transport controls */}
            {(() => {
              // Check that ALL files are from cloud storage (Dropbox/Google Drive) - not just the first one
              // This ensures the panel is hidden if any local files are added
              const isCloudStorage = loadedFiles.length > 0 &&
                loadedFiles.every(file => {
                  // Files must have a shareableUrl and it must be from Dropbox or Google Drive
                  const shareableUrl = file.shareableUrl;
                  if (!shareableUrl || shareableUrl === '' || shareableUrl.length === 0) return false;
                  return (shareableUrl.includes('dropbox.com') || shareableUrl.includes('dropboxusercontent.com')) ||
                         (shareableUrl.includes('drive.google.com') || shareableUrl.includes('googleusercontent.com'));
                });
              return (!isBlindTestMode && loadedFiles.length > 0 && loadedFiles.length <= 2 && selectedPlayer > 0 && isCloudStorage) ? (
                <Box
                  position="absolute"
                  top={0}
                  left="50%"
                  transform="translateX(-50%) translateY(-100%)"
                  w="100%"
                  maxW="2xl"
                  px={4}
                  zIndex={3}
                >
                  <ExpandedNotePanel 
                    trackNumber={selectedPlayer} 
                    fileId={loadedFiles[selectedPlayer - 1]?.id}
                  />
                </Box>
              ) : null;
            })()}
            <Container maxW="container.lg">
              <TransportControls
                onPlayPause={isPlaying ? pauseAll : playAll}
                onForward={forward}
                onRewind={rewind}
                onRewindToLastStart={rewindToLastStart}
                isPlaying={isPlaying}
                shareData={shareData}
                playerCount={audioElements.size}
                onRemove={handlePlayerRemove}
                onFilesSelected={handleFilesSelected}
                loadedFiles={loadedFiles}
                onAddFilesLoadingChange={setIsAddingFiles}
                onAddFilesProgressChange={(downloaded, total) => {
                  setAddingFilesDownloadedBytes(downloaded);
                  setAddingFilesTotalBytes(total);
                }}
                onFilterModalOpen={onFilterModalOpen}
                onToggleFilter={toggleMasterFilter}
                hasFilterBeenSet={hasFilterBeenSet}
                onCreateRegion={handleCreateRegion}
              />
            </Container>
          </Box>
        )}

        <SiteInfoModal isOpen={isPrivacyModalOpen} onClose={onPrivacyModalClose} onShowInfoOverlay={handleShowInfoOverlay} />
        
        {/* Master Filter Modal */}
        <MasterFilterModal
          isOpen={isFilterModalOpen}
          onClose={onFilterModalClose}
          filterType={filterType}
          filterFrequency={filterFrequency}
          filterQ={filterQ}
          filterSlope={filterSlope}
          filterFrequencyLow={filterFrequencyLow}
          filterFrequencyHigh={filterFrequencyHigh}
          filterInputGain={filterInputGain}
          onFilterChange={setMasterFilter}
          onReset={resetMasterFilter}
        />
        
        {/* Region Setup Modal */}
        {(() => {
          const modalData = (window as any).__regionModalData;
          if (!modalData) return null;
          return (
            <RegionSetupModal
              isOpen={isRegionModalOpen}
              onClose={onRegionModalClose}
              fileId={modalData.fileId}
              currentTime={modalData.currentTime}
              hasOpenRegion={modalData.hasOpenRegion || false}
              editingRegion={modalData.editingRegion || null}
            />
          );
        })()}
        
        {/* Mobile Download Warning for Shared Setups */}
        <MobileDownloadWarning
          isOpen={showSharedSetupMobileWarning}
          onClose={handleSharedSetupMobileWarningClose}
          onContinue={handleSharedSetupMobileWarningContinue}
          sizeMB={Math.round(pendingSharedSetupSize / (1024 * 1024))}
          shouldWarnDataUsage={pendingSharedSetupSize > 0 && getMobileDownloadWarnings(pendingSharedSetupSize).shouldWarnDataUsage}
          shouldWarnLargeSize={pendingSharedSetupSize > 0 && getMobileDownloadWarnings(pendingSharedSetupSize).shouldWarnLargeSize}
          networkType={pendingSharedSetupSize > 0 ? getMobileDownloadWarnings(pendingSharedSetupSize).networkInfo.effectiveType : 'unknown'}
        />

        {/* Selective File Loading Modal for Large Shared Setups */}
        <SelectiveFileLoadingModal
          isOpen={showSelectiveFileModal}
          onClose={handleSelectiveFileModalClose}
          onLoadAll={handleSelectiveFileLoadAll}
          onLoadSelected={handleSelectiveFileLoadSelected}
          files={selectiveFileList}
          totalSizeMB={selectiveFileData ? (() => {
            try {
              const decompressedData = LZString.decompressFromEncodedURIComponent(selectiveFileData);
              const shareData = JSON.parse(decompressedData) as ShareData;
              return shareData.totalFileSize ? shareData.totalFileSize / (1024 * 1024) : 0;
            } catch {
              return 0;
            }
          })() : 0}
        />
      </Flex>
    </SyncProvider>
  );
};

const App = () => {
  return (
    <ChakraProvider theme={theme}>
      <GoogleAnalytics />
      <BrowserRouter>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/KMDeck" element={<KMDeck />} />
          <Route path="/embed" element={<Embed />} />
          <Route path="/patchplan" element={<PatchPlan />} />
          <Route path="/*" element={
            <SharedDataProvider>
              <AudioProvider>
                <NotesProvider>
                  <DisplayNamesProvider>
                    <RegionsProvider>
                      <PageTitleProvider>
                        <AppContent />
                      </PageTitleProvider>
                    </RegionsProvider>
                  </DisplayNamesProvider>
                </NotesProvider>
              </AudioProvider>
            </SharedDataProvider>
          } />
        </Routes>
      </BrowserRouter>
    </ChakraProvider>
  );
};

export default App;
